import { useState, useEffect } from 'react'
import { Check } from 'lucide-react'

const DEFAULT_MAX_SELECTIONS = 4

const AssessmentCard = ({ assessment, isSelected, onToggle, getScoreColor, formatDate, isSameClient, disabled }) => (
  <button
    onClick={() => onToggle(assessment.id)}
    disabled={disabled && !isSelected}
    aria-pressed={isSelected}
    className={`w-full text-left p-4 border rounded-lg transition-colors ${
      disabled && !isSelected ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-500 hover:bg-blue-50 cursor-pointer'
    } ${isSelected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-300' : ''} ${
      isSameClient && !isSelected ? 'border-blue-200 border-l-4 border-l-blue-500' : 'border-gray-200'
    }`}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center space-x-3">
          <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
            isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
          }`}>
            {isSelected && <Check className="w-3 h-3 text-white" />}
          </div>
          <h4 className="text-sm font-semibold text-gray-900">
            {assessment.clientInfo.firstName} {assessment.clientInfo.lastName}
          </h4>
          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getScoreColor(assessment.scores.average)}`}>
            {assessment.scores.average.toFixed(1)}/10
          </span>
          {isSameClient && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              Même client
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-1 ml-8">
          {assessment.clientInfo.company}
        </p>
        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500 ml-8">
          <span>{assessment.clientInfo.email}</span>
          {assessment.clientInfo.clientId && (
            <span>N° {assessment.clientInfo.clientId}</span>
          )}
          <span>{formatDate(assessment.clientInfo.assessmentDate)}</span>
          {assessment.questionnaireVersion ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              v{assessment.questionnaireVersion}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
              legacy
            </span>
          )}
        </div>
      </div>
    </div>
  </button>
)

const AssessmentSelector = ({
  excludeIds = [],
  currentClientId,
  maxSelections = DEFAULT_MAX_SELECTIONS,
  onSelect,
  onClose,
  /** @deprecated use excludeIds instead */
  currentAssessmentId,
}) => {
  const idsToExclude = excludeIds.length > 0
    ? excludeIds
    : currentAssessmentId ? [currentAssessmentId] : []

  const [assessments, setAssessments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIds, setSelectedIds] = useState([])

  useEffect(() => {
    fetchAssessments()
  }, [])

  const fetchAssessments = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('adminToken')
      const response = await fetch('/api/admin/assessments?limit=100', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        const excludeSet = new Set(idsToExclude)
        setAssessments(data.data.assessments.filter(a => !excludeSet.has(a.id)))
      } else {
        throw new Error('Erreur lors du chargement des évaluations')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100'
    if (score >= 6) return 'text-yellow-600 bg-yellow-100'
    if (score >= 4) return 'text-orange-600 bg-orange-100'
    return 'text-red-600 bg-red-100'
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric'
    })
  }

  const toggleSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const atLimit = selectedIds.length >= maxSelections

  const handleConfirm = () => {
    if (selectedIds.length >= 1) {
      onSelect(selectedIds)
    }
  }

  const filteredAssessments = assessments.filter(a => {
    const s = searchTerm.toLowerCase()
    return (
      a.clientInfo.firstName.toLowerCase().includes(s) ||
      a.clientInfo.lastName.toLowerCase().includes(s) ||
      a.clientInfo.company.toLowerCase().includes(s) ||
      a.clientInfo.email.toLowerCase().includes(s) ||
      (a.clientInfo.clientId && a.clientInfo.clientId.toLowerCase().includes(s))
    )
  })

  const normalizedCurrentClientId = currentClientId?.trim().toUpperCase()
  const hasSameClientGroup = Boolean(normalizedCurrentClientId)

  const sameClientAssessments = hasSameClientGroup
    ? filteredAssessments.filter(a =>
        a.clientInfo.clientId &&
        a.clientInfo.clientId.trim().toUpperCase() === normalizedCurrentClientId
      )
    : []

  const otherAssessments = hasSameClientGroup
    ? filteredAssessments.filter(a =>
        !a.clientInfo.clientId ||
        a.clientInfo.clientId.trim().toUpperCase() !== normalizedCurrentClientId
      )
    : filteredAssessments

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Sélectionner des évaluations à comparer"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Sélectionner des évaluations à comparer
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Choisissez jusqu'à {maxSelections} évaluation{maxSelections > 1 ? 's' : ''} à ajouter
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
            aria-label="Fermer"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-200">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher par nom, entreprise, email ou N° dossier..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            aria-label="Rechercher des évaluations"
          />
        </div>

        {/* Selection count bar */}
        {selectedIds.length > 0 && (
          <div className="px-6 py-2 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
            <span className="text-sm font-medium text-blue-800">
              {selectedIds.length} évaluation{selectedIds.length > 1 ? 's' : ''} sélectionnée{selectedIds.length > 1 ? 's' : ''}
              {atLimit && <span className="text-blue-600 ml-2">(maximum atteint)</span>}
            </span>
            <button
              onClick={() => setSelectedIds([])}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Tout désélectionner
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-gray-600">Chargement...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg" role="alert">
              {error}
            </div>
          )}

          {!loading && !error && filteredAssessments.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600">
                {searchTerm ? 'Aucune évaluation ne correspond à votre recherche' : 'Aucune autre évaluation disponible'}
              </p>
            </div>
          )}

          {!loading && !error && filteredAssessments.length > 0 && (
            <div className="space-y-4">
              {sameClientAssessments.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                    <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-sm font-medium text-blue-800">
                      Même dossier client — N° {currentClientId}
                    </span>
                    <span className="text-xs text-blue-600">
                      ({sameClientAssessments.length} évaluation{sameClientAssessments.length > 1 ? 's' : ''})
                    </span>
                  </div>
                  <div className="space-y-3">
                    {sameClientAssessments.map((assessment) => (
                      <AssessmentCard
                        key={assessment.id}
                        assessment={assessment}
                        isSelected={selectedIds.includes(assessment.id)}
                        onToggle={toggleSelection}
                        getScoreColor={getScoreColor}
                        formatDate={formatDate}
                        isSameClient
                        disabled={atLimit}
                      />
                    ))}
                  </div>
                </div>
              )}

              {otherAssessments.length > 0 && (
                <div>
                  {sameClientAssessments.length > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg mb-3 mt-2">
                      <span className="text-sm font-medium text-gray-600">Autres évaluations</span>
                      <span className="text-xs text-gray-500">({otherAssessments.length})</span>
                    </div>
                  )}
                  <div className="space-y-3">
                    {otherAssessments.map((assessment) => (
                      <AssessmentCard
                        key={assessment.id}
                        assessment={assessment}
                        isSelected={selectedIds.includes(assessment.id)}
                        onToggle={toggleSelection}
                        getScoreColor={getScoreColor}
                        formatDate={formatDate}
                        disabled={atLimit}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {selectedIds.length === 0
              ? 'Sélectionnez au moins 1 évaluation'
              : `${selectedIds.length} évaluation${selectedIds.length > 1 ? 's' : ''} sélectionnée${selectedIds.length > 1 ? 's' : ''} — ${selectedIds.length + idsToExclude.length} au total`}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedIds.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Ajouter ({selectedIds.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AssessmentSelector
